import { OENPanel } from "../enums/oe-panel.enum";
import { OENInputControl } from "../enums/oe-input-control.enum";
import { OENLockedState } from "../enums/oe-locked-state.enum";
import { OENLockingProfileType } from "../enums/oe-locking-profile-type.enum";

export class OEInputLockStateItemModel {
  constructor(
    public readonly panel: OENPanel,
    public readonly input: OENInputControl,
    public readonly lockState: OENLockedState = OENLockedState.NONE,
  ) {} // locked = 0, unlocked = 1; none = 2
}

export class OELockingProfileTransportModel {
  public profile: Array<OEInputLockStateItemModel> = null;
  public profileType: OENLockingProfileType = OENLockingProfileType.PERSISTENT;
}

export class OELockingProfileModel {
  public profile: Array<OEInputLockStateItemModel> = null;
  public profileType: OENLockingProfileType = OENLockingProfileType.PERSISTENT;

  constructor() {
    this.profile = new Array<OEInputLockStateItemModel>();
  }

  public updateInputLockState(inputLockState: OEInputLockStateItemModel): void {
    let index = this.profile
      .map((item: OEInputLockStateItemModel) => {
        return item.input;
      })
      .indexOf(inputLockState.input);
    if (index >= 0) {
      this.profile.splice(index, 1, inputLockState);
    } else {
      this.profile.push(inputLockState);
    }
  }

  public removeInputLockState(inputControl: OENInputControl): void {
    let index = this.profile
      .map((item: OEInputLockStateItemModel) => {
        return item.input;
      })
      .indexOf(inputControl);
    if (index >= 0) {
      this.profile.splice(index, 1);
    }
  }

  public createInputLockState(
    _panel: OENPanel,
    _input: OENInputControl,
    _lockState: OENLockedState,
  ): void {
    if (_panel != null && _input != null && _lockState != null) {
      let inputLockState: OEInputLockStateItemModel = new OEInputLockStateItemModel(
        _panel,
        _input,
        _lockState,
      );
      this.updateInputLockState(inputLockState);
    }
  }

  public getLockProfileForPanel(_panel: OENPanel): Array<OEInputLockStateItemModel> {
    return this.profile.filter((item) => {
      return item.panel === _panel;
    });
  }

  public getLockProfile(): Array<OEInputLockStateItemModel> {
    return this.profile;
  }

  public getLockProfileForInputElement(_input: OENInputControl): OEInputLockStateItemModel {
    let itemLockState = this.profile.filter((item) => {
      return item.input === _input;
    });
    if (itemLockState != null && itemLockState.length > 0) {
      return itemLockState[0];
    }
    return null;
  }

  public getLockingProfileType(): OENLockingProfileType {
    return this.profileType;
  }

  public setLockingProfileType(_profileType: OENLockingProfileType): void {
    this.profileType = _profileType;
  }
}

export class OEPrivatizationLockingProfileModel extends OELockingProfileModel {
  // merge with strategy
  public hasPrivatization: boolean = false;

  constructor() {
    super();
  }

  public static getLockIntersection(
    strategy: OELockingProfileModel,
    privatization: OEPrivatizationLockingProfileModel,
  ) {
    if (!privatization.hasPrivatization) {
      return strategy;
    }

    let intersection = new OELockingProfileModel();
    let intersectionMap = new Map<
      string,
      {
        strategy: OEInputLockStateItemModel;
        privatization: OEInputLockStateItemModel;
      }
    >();

    strategy.profile.forEach((p) => {
      const k = OENInputControl[p.input];

      intersectionMap.set(k, { strategy: p, privatization: null });
    });

    privatization.profile.forEach((p) => {
      const k = OENInputControl[p.input];

      if (intersectionMap.has(k)) {
        const v = intersectionMap.get(k);
        v.privatization = p;
      } else {
        intersectionMap.set(k, { strategy: null, privatization: p });
      }
    });

    for (let v of intersectionMap.values()) {
      let strategyProfile: OEInputLockStateItemModel = v.strategy;
      let privatizationProfile: OEInputLockStateItemModel = v.privatization;
      let intersectionLockState;
      let panel = strategyProfile != null ? strategyProfile.panel : privatizationProfile.panel;
      let input = strategyProfile != null ? strategyProfile.input : privatizationProfile.input;
      let strategyLockState =
        strategyProfile != null ? strategyProfile.lockState : OENLockedState.NONE;
      let privatizationLockState =
        privatizationProfile != null ? privatizationProfile.lockState : OENLockedState.NONE;

      if (
        strategyLockState === OENLockedState.LOCKED ||
        privatizationLockState === OENLockedState.LOCKED
      ) {
        // if either is locked, stay locked
        intersectionLockState = OENLockedState.LOCKED;
      } else if (
        strategyLockState === OENLockedState.UNLOCKED ||
        privatizationLockState === OENLockedState.UNLOCKED
      ) {
        // if neither is locked, and at least one is unlocked, stay unlocked
        intersectionLockState = OENLockedState.UNLOCKED;
      } else {
        // otherwise
        intersectionLockState = OENLockedState.NONE;
      }

      intersection.profile.push(new OEInputLockStateItemModel(panel, input, intersectionLockState));
    }

    return intersection;
  }
}
